/**
 * Library Effect Service
 *
 * Loads and caches library animation frames for combat effect rendering.
 * Provides on-demand loading with caching for efficiency.
 */

interface LibraryAnimationData {
  animationId: string;
  frames: string[]; // base64 encoded PNGs
  frameCount: number;
}

/**
 * Service for loading library effect animations
 */
export class LibraryEffectService {
  private cache: Map<string, LibraryAnimationData> = new Map();
  private loadingPromises: Map<string, Promise<LibraryAnimationData | null>> = new Map();
  private preloadPromise: Promise<void> | null = null;

  // Combat effects that we preload for instant triggering
  private static readonly COMBAT_EFFECTS = [
    // Batch 1 & 2: Triggered ability effects
    'lifesteal_effect',
    'execute_effect',
    'cleanse_effect',
    'shield_effect',
    'reflect_effect',
    // Batch 4: Strike effects (ground-targeted abilities)
    'strike_telegraph',
    'lightning_strike_effect',
    'meteor_effect',
    'earthquake_effect',
    'arrow_rain_effect',
    'holy_smite_effect',
    'volcanic_eruption_effect',
    'ice_storm_effect',
    'shadow_bolt_effect'
  ];

  /**
   * Preload all combat effect animations
   * Call this during combat initialization for instant effect playback
   */
  async preloadCombatEffects(): Promise<void> {
    if (this.preloadPromise) {
      return this.preloadPromise;
    }

    this.preloadPromise = (async () => {
      console.log('[LibraryEffectService] Preloading combat effects...');

      const results = await Promise.allSettled(
        LibraryEffectService.COMBAT_EFFECTS.map(id => this.loadAnimation(id))
      );

      const loaded = results.filter(r => r.status === 'fulfilled' && r.value).length;
      console.log(`[LibraryEffectService] Preloaded ${loaded}/${LibraryEffectService.COMBAT_EFFECTS.length} combat effects`);
    })();

    return this.preloadPromise;
  }

  /**
   * Get animation frames for a library effect
   * Returns cached data or loads from API
   */
  async getAnimationFrames(animationId: string): Promise<string[] | null> {
    const data = await this.loadAnimation(animationId);
    return data?.frames ?? null;
  }

  /**
   * Check if an animation is cached
   */
  isCached(animationId: string): boolean {
    return this.cache.has(animationId);
  }

  /**
   * Get cached frames synchronously (returns null if not cached)
   */
  getCachedFrames(animationId: string): string[] | null {
    return this.cache.get(animationId)?.frames ?? null;
  }

  /**
   * Load animation from API with caching and deduplication
   */
  private async loadAnimation(animationId: string): Promise<LibraryAnimationData | null> {
    // Return cached if available
    if (this.cache.has(animationId)) {
      return this.cache.get(animationId)!;
    }

    // Deduplicate concurrent requests
    if (this.loadingPromises.has(animationId)) {
      return this.loadingPromises.get(animationId)!;
    }

    const loadPromise = this.fetchAnimation(animationId);
    this.loadingPromises.set(animationId, loadPromise);

    try {
      const result = await loadPromise;
      if (result) {
        this.cache.set(animationId, result);
      }
      return result;
    } finally {
      this.loadingPromises.delete(animationId);
    }
  }

  /**
   * Fetch animation from backend API
   */
  private async fetchAnimation(animationId: string): Promise<LibraryAnimationData | null> {
    try {
      const response = await fetch(`/api/library-animations/${animationId}`);

      if (!response.ok) {
        console.warn(`[LibraryEffectService] Animation not found: ${animationId}`);
        return null;
      }

      const data = await response.json();

      return {
        animationId: data.animationId,
        frames: data.frames,
        frameCount: data.frameCount
      };
    } catch (error) {
      console.error(`[LibraryEffectService] Failed to load animation: ${animationId}`, error);
      return null;
    }
  }

  /**
   * Clear cache (for cleanup)
   */
  clearCache(): void {
    this.cache.clear();
    this.preloadPromise = null;
  }
}

// Singleton instance
let instance: LibraryEffectService | null = null;

export function getLibraryEffectService(): LibraryEffectService {
  if (!instance) {
    instance = new LibraryEffectService();
  }
  return instance;
}
